import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { validateMobile } from '../../utils/phoneValidation';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import MobileInput from '../ui/mobile-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import OtpVerificationForm, { setOtpResendCooldown } from '../shared/OtpVerificationForm';

const OTP_EXPIRY_MINUTES = 10;

export default function VerifyMobileDialog({
  open,
  onOpenChange,
  initialPhone = '',
  mode = 'verify',
  onVerified,
}) {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState(initialPhone);
  const [otp, setOtp] = useState('');
  const [serverError, setServerError] = useState('');
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (open) {
      setStep('phone');
      setPhone(initialPhone || '');
      setOtp('');
      setServerError('');
      setResendCooldown(60);
    }
  }, [open, initialPhone]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    setServerError('');

    const phoneErr = validateMobile(phone, { required: true });
    if (phoneErr) {
      setServerError(phoneErr);
      return;
    }

    setSending(true);
    try {
      const res = await api.post('/auth/phone/send-otp', { phone });
      const payload = res.data?.data ?? res.data;
      if (payload?.user) {
        onVerified?.(payload.user, { pending: true });
      }
      setStep('otp');
      setOtp('');
      setResendCooldown(60);
    } catch (err) {
      setServerError(err.response?.data?.message || 'Could not send verification code');
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setServerError('');

    if (otp.length !== 6) {
      setServerError('Enter the 6-digit verification code');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/auth/phone/verify-otp', { phone, otp });
      const payload = res.data?.data ?? res.data;
      const userPayload = payload?.user ?? {
        phone: payload?.phone ?? phone,
        phone_verified: true,
        phone_verified_at: payload?.phone_verified_at ?? new Date().toISOString(),
      };
      onVerified?.(userPayload);
      onOpenChange(false);
    } catch (err) {
      setServerError(err.response?.data?.message || 'Verification failed');
      setOtp('');
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    setServerError('');
    setResending(true);
    try {
      await api.post('/auth/phone/resend-otp', { phone });
      setOtp('');
      setResendCooldown(60);
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not resend code';
      const retryAfter = err.response?.data?.data?.retry_after;
      setOtpResendCooldown(setResendCooldown, retryAfter);
      setServerError(msg);
    } finally {
      setResending(false);
    }
  };

  const title = mode === 'change' ? 'Change mobile number' : 'Verify mobile number';
  const description = step === 'phone'
    ? 'Enter your 10-digit mobile number. We will send a verification code via SMS.'
    : `Enter the 6-digit code sent to ${phone}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {step === 'phone' ? (
          <form onSubmit={sendOtp} className="space-y-4">
            {serverError && (
              <p className="text-sm text-destructive">{serverError}</p>
            )}
            <div>
              <Label>Mobile number</Label>
              <MobileInput value={phone} onChange={setPhone} disabled={sending} />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending}>
                {sending ? 'Sending…' : 'Send OTP'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <OtpVerificationForm
            otp={otp}
            onOtpChange={setOtp}
            onSubmit={verifyOtp}
            onResend={resendOtp}
            onCancel={() => onOpenChange(false)}
            submitting={submitting}
            resending={resending}
            serverError={serverError}
            cooldownSeconds={resendCooldown}
            expiryMinutes={OTP_EXPIRY_MINUTES}
            submitLabel="Verify OTP"
            submittingLabel="Verifying…"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
